import { absBig, BIG_ONE, BIG_ZERO, gcdBig } from "@/lib/tool-kit/math/bigint";
import {
  type RadicalFraction,
  sqrtOfFraction,
} from "@/lib/tool-kit/math/radical";
import {
  addR,
  commonDenominator,
  divR,
  formatStyled,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  type Rational,
  signR,
  subR,
  toNumberR,
  writesAsFraction,
} from "@/lib/tool-kit/math/rational";

/**
 * Everything about the line through two points, kept exact.
 *
 * Coordinates are rationals, so the slope of (1, 2)–(4, 6) is 4/3 and stays 4/3 in
 * every equation built from it; decimals are derived at the end, never fed back in.
 */

export interface Point {
  x: Rational;
  y: Rational;
}

export type LineKind = "oblique" | "horizontal" | "vertical";

export interface StandardForm {
  A: bigint;
  B: bigint;
  C: bigint;
}

export interface SlopeResult {
  p1: Point;
  p2: Point;
  /** Run and rise: x₂ − x₁ and y₂ − y₁. */
  dx: Rational;
  dy: Rational;
  kind: LineKind;
  /** Null for a vertical line, whose slope is undefined. */
  slope: Rational | null;
  /** Angle of inclination, 0° ≤ θ < 180°, measured counterclockwise from +x. */
  angleDegrees: number;
  /** arctan(m) in degrees, between −90° and 90°; null when vertical. */
  arctanDegrees: number | null;
  distance: RadicalFraction;
  distanceDecimal: number;
  midpoint: Point;
  /** b in y = mx + b; null when vertical. */
  intercept: Rational | null;
  /** Ax + By = C with whole numbers, no common factor, A ≥ 0 (B > 0 if A = 0). */
  standard: StandardForm;
  /** −1/m; null when horizontal (the perpendicular is vertical). */
  perpendicular: Rational | null;
}

const DEGREES = 180 / Math.PI;

function standardForm(p1: Point, dx: Rational, dy: Rational): StandardForm {
  // dy·x − dx·y = dy·x₁ − dx·y₁, cleared of fractions and common factors.
  const constant = subR(mulR(dy, p1.x), mulR(dx, p1.y));
  const terms = [dy, negR(dx), constant];
  const lcd = commonDenominator(terms);
  const whole = terms.map((t) => (t.num * lcd) / t.den);
  const common = gcdBig(gcdBig(whole[0], whole[1]), whole[2]) || BIG_ONE;
  let [A, B, C] = whole.map((w) => w / common);
  if (A < BIG_ZERO || (A === BIG_ZERO && B < BIG_ZERO)) {
    A = -A;
    B = -B;
    C = -C;
  }
  return { A, B, C };
}

function lineKind(dx: Rational, dy: Rational): LineKind {
  if (isZeroR(dx)) {
    return "vertical";
  }
  return isZeroR(dy) ? "horizontal" : "oblique";
}

/** Null when the two points coincide: infinitely many lines pass through one point. */
export function computeSlope(p1: Point, p2: Point): SlopeResult | null {
  const dx = subR(p2.x, p1.x);
  const dy = subR(p2.y, p1.y);
  if (isZeroR(dx) && isZeroR(dy)) {
    return null;
  }
  const kind = lineKind(dx, dy);
  const slope = kind === "vertical" ? null : divR(dy, dx);
  const squared = addR(mulR(dx, dx), mulR(dy, dy));
  const arctan = slope === null ? null : Math.atan(toNumberR(slope)) * DEGREES;
  let angle = 90;
  if (arctan !== null) {
    angle = arctan < 0 ? arctan + 180 : arctan;
  }
  return {
    p1,
    p2,
    dx,
    dy,
    kind,
    slope,
    angleDegrees: angle,
    arctanDegrees: arctan,
    distance: sqrtOfFraction(squared.num, squared.den),
    distanceDecimal: Math.hypot(toNumberR(dx), toNumberR(dy)),
    midpoint: {
      x: divR(addR(p1.x, p2.x), intR(2)),
      y: divR(addR(p1.y, p2.y), intR(2)),
    },
    intercept: slope === null ? null : subR(p1.y, mulR(slope, p1.x)),
    standard: standardForm(p1, dx, dy),
    perpendicular:
      slope === null || isZeroR(slope) ? null : negR(divR(intR(1), slope)),
  };
}

/* -------------------------------------------------------------- notation */

export type { NumberStyle } from "@/lib/tool-kit/math/rational";

/** A value in the chosen style; the name the slope widget uses. */
export const formatValue = formatStyled;

const isFractionText = writesAsFraction;

/** A coefficient in front of x: "", "−", "3", "(4/3)", "1.5". */
function coefficientText(
  m: Rational,
  style: NumberStyle,
  decimalMark: string
): string {
  if (m.num === m.den) {
    return "";
  }
  if (m.num === -m.den) {
    return "−";
  }
  const text = formatValue(m, style, decimalMark);
  return isFractionText(m, style) ? `(${text})` : text;
}

function magnitudeText(value: Rational, style: NumberStyle, mark: string) {
  return formatValue(signR(value) < 0 ? negR(value) : value, style, mark);
}

/** " + 2", " − 2/3", or "" for zero — a constant added on the right. */
function signedConstant(
  value: Rational,
  style: NumberStyle,
  decimalMark: string
): string {
  if (isZeroR(value)) {
    return "";
  }
  const magnitude = magnitudeText(value, style, decimalMark);
  return signR(value) < 0 ? ` − ${magnitude}` : ` + ${magnitude}`;
}

/** "x − 1", "x + 2", "x" — a variable minus a coordinate. */
function shifted(
  variable: string,
  value: Rational,
  style: NumberStyle,
  decimalMark: string
) {
  if (isZeroR(value)) {
    return variable;
  }
  const magnitude = magnitudeText(value, style, decimalMark);
  return signR(value) < 0
    ? `${variable} + ${magnitude}`
    : `${variable} − ${magnitude}`;
}

/** "y = (4/3)x + 2/3", "y = 5", "x = 2". */
export function slopeInterceptText(
  result: SlopeResult,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  if (result.slope === null) {
    return `x = ${formatValue(result.p1.x, style, decimalMark)}`;
  }
  const b = result.intercept ?? intR(0);
  if (isZeroR(result.slope)) {
    return `y = ${formatValue(b, style, decimalMark)}`;
  }
  return `y = ${coefficientText(result.slope, style, decimalMark)}x${signedConstant(b, style, decimalMark)}`;
}

/** "y − 2 = (4/3)(x − 1)"; null when the line is vertical. */
export function pointSlopeText(
  result: SlopeResult,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string | null {
  if (result.slope === null) {
    return null;
  }
  const left = shifted("y", result.p1.y, style, decimalMark);
  const xPart = shifted("x", result.p1.x, style, decimalMark);
  const factor = isZeroR(result.p1.x) ? xPart : `(${xPart})`;
  if (isZeroR(result.slope)) {
    return `${left} = 0`;
  }
  return `${left} = ${coefficientText(result.slope, style, decimalMark)}${factor}`;
}

function termText(coefficient: bigint, variable: string, first: boolean) {
  const size = absBig(coefficient);
  const body = size === BIG_ONE ? variable : `${size.toString()}${variable}`;
  if (first) {
    return coefficient < BIG_ZERO ? `−${body}` : body;
  }
  return coefficient < BIG_ZERO ? ` − ${body}` : ` + ${body}`;
}

/** "4x − 3y = −2" — Ax + By = C. */
export function standardFormText({ A, B, C }: StandardForm): string {
  const parts: string[] = [];
  if (A !== BIG_ZERO) {
    parts.push(termText(A, "x", true));
  }
  if (B !== BIG_ZERO) {
    parts.push(termText(B, "y", parts.length === 0));
  }
  const right = C < BIG_ZERO ? `−${(-C).toString()}` : C.toString();
  return `${parts.join("")} = ${right}`;
}
